// ./src/components/generic/form/AutocompleteField.tsx
//
// Free text with suggestions (Mantine Autocomplete) on the shared `(name, value)` change contract.
//
// ⚠ ON A PHONE IT IS THE FULL-SCREEN PICKER, as `SelectField` is: the suggestions are its rows, and a pinned
//   "Use “…”" row keeps whatever was typed, so the field still takes text that is not a suggestion. Desktop is always
//   Mantine's Autocomplete.

import React, { useMemo, useRef, useState } from 'react';
import { Autocomplete, AutocompleteProps } from '@mantine/core';

import { useAppEnvironment } from '../AppEnvironmentContext';

import { FieldChangeHandler } from './types';
import { OptionPickerModal } from './OptionPickerModal';
import { PhonePickerTrigger } from './SelectField';
import { usesPhonePicker } from './pickerRules';

export interface AutocompleteFieldProps extends Omit<AutocompleteProps, 'onChange' | 'value'> {
  name?: string;
  value?: string;
  onChange?: FieldChangeHandler<string>;
  /** The phone picker's "keep what I typed" row, given the text (default: Use “text”). */
  keepTypedLabel?: (text: string) => string;
  /** The phone picker's surface style (the public event site passes its theme, `usePortalSurfaceStyle`). */
  modalContentStyle?: React.CSSProperties;
}

export const AutocompleteField: React.FC<AutocompleteFieldProps> = ({ name, value, onChange, keepTypedLabel, modalContentStyle, ...rest }) => {
  const { isMobile } = useAppEnvironment();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // A pick (a suggestion, "Use …", Clear) closes the picker too — its close must not then overwrite it with the text.
  const picked = useRef(false);

  // The modal does not filter a search it does not own — this one is owned here, so it can label the "Use" row.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items = Array.isArray(rest.data) ? (rest.data as unknown[]) : [];

    const text = (it: unknown): string => {
      if (it && typeof it === 'object') {
        const o = it as { label?: unknown; value?: unknown };

        return String(o.label ?? o.value ?? '');
      }

      return String(it);
    };

    return q ? items.filter((it) => text(it).toLowerCase().includes(q)) : items;
  }, [rest.data, search]);

  if (usesPhonePicker({ isMobile, disabled: rest.disabled, readOnly: rest.readOnly })) {
    const typed = search.trim();

    return (
      <PhonePickerTrigger
        {...(rest as object)}
        displayValue={value ?? ''}
        onOpen={() => {
          // The picker opens on the current text, so a small change to it is a small edit.
          setSearch(value ?? '');
          picked.current = false;
          setPickerOpen(true);
        }}
      >
        <OptionPickerModal
          opened={pickerOpen}
          onClose={() => {
            // What was typed is kept on close, as a desktop keeps every keystroke (#138 review: closing dropped it).
            // A pick or "Use" has already set the value, so this changes nothing then.
            setPickerOpen(false);

            if (!picked.current && search.trim() !== '' && search.trim() !== (value ?? '')) {
              onChange?.(name, search.trim());
            }
          }}
          data={filtered}
          value={value ?? null}
          onSelect={(v) => {
            picked.current = true;
            onChange?.(name, v ?? '');
          }}
          title={rest.label ?? rest.placeholder ?? (rest as { 'aria-label'?: string })['aria-label']}
          searchPlaceholder={typeof rest.placeholder === 'string' ? rest.placeholder : undefined}
          searchValue={search}
          onSearchChange={setSearch}
          clearable={!!value}
          nothingFoundMessage={null}
          contentStyle={modalContentStyle}
          createLabel={typed ? (keepTypedLabel ? keepTypedLabel(typed) : `Use “${typed}”`) : undefined}
          onCreate={(text) => {
            picked.current = true;
            setPickerOpen(false);
            onChange?.(name, text);
          }}
        />
      </PhonePickerTrigger>
    );
  }

  return <Autocomplete {...rest} value={value} onChange={(v) => onChange?.(name, v)} />;
};
